// @src/pre-deployment/src/contexts/AuthContext.js v1.2
// - Firebase authentication context for pre-deployment environment
// - Simplified version focused on registration flow
// - Integrates with main Firebase configuration
// - Includes ProtectedRoute component for route guarding
// - v1.2: Blank-screen fix – show loading UI when loading is true; call setLoading(false) in profile fetch catch so app always renders

import {
  onAuthStateChanged,
  signInWithEmailLink,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  updatePassword,
  signOut
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { auth, db } from '../services/firebase';
import { getEmailActionSettings } from '../utils/emailTemplates';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationStep, setRegistrationStep] = useState('email'); // email, password, assessment, complete
  const [uiFont, setUiFont] = useState(() => {
    if (typeof window === 'undefined') return 'bebas';
    try {
      return localStorage.getItem('uiFontPreference') || 'bebas';
    } catch (e) {
      console.warn('⚠️ Failed to read uiFontPreference from localStorage:', e);
      return 'bebas';
    }
  });



  console.group('🔐 PRE-DEPLOYMENT AUTH: AuthProvider initialized');
  console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
  console.log('🔧 Function: AuthProvider');
  console.log('✨ Version 1.1 Updates:');
  console.log('  - Integrated ProtectedRoute component');
  console.log('  - Enhanced logging throughout');
  console.log('  - Route protection logic centralized');
  console.log('Pre-deployment auth context started');
  console.groupEnd();

  useEffect(() => {


    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.group('🔐 PRE-DEPLOYMENT AUTH: Auth state changed');
      console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
      console.log('🔧 Function: onAuthStateChanged callback');
      console.log('User:', user ? `${user.uid} (${user.email})` : 'null');
      console.log('User authenticated:', !!user);



      setLoading(true);



      setUser(user);

      if (user) {
        try {
          console.log('📊 Fetching user profile from Firestore...');
          console.log('🗂️ Firestore path: users/' + user.uid);
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('✅ User profile found in Firestore');
            console.log('📦 User profile data:', userData);
            console.log('🔑 Profile flags:', {
              passwordSet: userData.passwordSet,
              assessmentComplete: userData.assessmentComplete,
              profileComplete: userData.profileComplete,
              isAdmin: userData.isAdmin || false
            });
            setUserProfile(userData);

            // Determine registration step based on user data
            if (!userData.passwordSet) {
              console.log('📍 Registration step: password (password not set)');
              setRegistrationStep('password');
            } else if (!userData.assessmentComplete) {
              console.log('📍 Registration step: assessment (assessment not complete)');
              setRegistrationStep('assessment');
            } else if (!userData.profileComplete) {
              console.log('📍 Registration step: complete (profile not complete)');
              setRegistrationStep('complete');
            } else {
              console.log('📍 Registration step: dashboard (fully registered)');
              setRegistrationStep('dashboard');
            }
          } else {
            console.warn('⚠️ No user profile found in Firestore');
            console.log('📊 User exists in Auth but no Firestore document');
            console.log('📍 Registration step: email (no profile)');
            setUserProfile(null);
            setRegistrationStep('email');
          }
        } catch (error) {
          console.error('❌ Error fetching user profile:', error);
          console.log('📊 Error details:', {
            message: error.message,
            code: error.code,
            stack: error.stack
          });



          setLoading(false);
        }
      } else {
        console.log('📊 No authenticated user');
        setUserProfile(null);
        setRegistrationStep('email');
      }



      setLoading(false);
      console.log('✅ Auth state update complete');
      console.groupEnd();
    });

    return unsubscribe;
  }, []);

  // Sync nav-only font (sidebar affects only AppHomepage, LevelPage, ChapterPage)
  useEffect(() => {
    const FONT_MAP = {
      bebas: "'Bebas Neue', system-ui, sans-serif",
      rajdhani: "'Rajdhani', system-ui, sans-serif",
      teko: "'Teko', system-ui, sans-serif",
      inter: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      robotoCondensed: "'Roboto Condensed', system-ui, sans-serif"
    };
    const family = FONT_MAP[uiFont] || FONT_MAP.bebas;
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--font-family-nav', family);
    }
  }, [uiFont]);

  // Check if user already exists
  const checkUserExists = async (email) => {
    console.group('🔍 PRE-DEPLOYMENT AUTH: checkUserExists');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: checkUserExists');
    console.log('📧 Checking if user exists:', email);
    console.log('🗂️ Firestore path: users collection (query by userEmail)');

    try {
      // Check Firestore for existing user
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('userEmail', '==', email));
      console.log('📊 Executing Firestore query...');
      const querySnapshot = await getDocs(q);

      const exists = !querySnapshot.empty;
      console.log('✅ Query completed');
      console.log('📊 User exists in Firestore:', exists);

      if (exists) {
        const userData = querySnapshot.docs[0].data();
        console.log('✅ Existing user data found:', userData);
        console.log('🔑 User flags:', {
          passwordSet: userData.passwordSet,
          profileComplete: userData.profileComplete
        });
        console.groupEnd();
        return {
          exists: true,
          userData,
          passwordSet: userData.passwordSet,
          profileComplete: userData.profileComplete
        };
      }

      console.log('📊 No existing user found');
      console.groupEnd();
      return { exists: false };
    } catch (error) {
      console.error('❌ Error checking user existence:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      console.groupEnd();
      return { exists: false, error: error.message };
    }
  };

  // Send registration email link
  const sendRegistrationLink = async (email) => {
    console.group('📧 PRE-DEPLOYMENT AUTH: sendRegistrationLink');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: sendRegistrationLink');
    console.log('📧 Sending registration link to:', email);

    try {
      // First check if user already exists
      console.log('🔍 Checking if user already exists...');
      const userCheck = await checkUserExists(email);
      if (userCheck.exists) {
        console.warn('⚠️ User already exists');
        console.log('📊 User check result:', userCheck);
        console.groupEnd();
        return {
          success: false,
          error: 'User already exists. Please use the login form instead.',
          userExists: true
        };
      }

      const actionCodeSettings = getEmailActionSettings(`${window.location.origin}/setup-password`);
      console.log('📧 Action code settings:', actionCodeSettings);
      console.log('📧 Redirect URL:', actionCodeSettings.url);

      console.log('📤 Sending sign-in link via Firebase Auth...');
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      localStorage.setItem('emailForSignIn', email);
      console.log('✅ Registration link sent successfully');
      console.log('💾 Email stored in localStorage for verification');
      console.groupEnd();
      return { success: true };
    } catch (error) {
      console.error('❌ Error sending registration link:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      console.groupEnd();
      return { success: false, error: error.message };
    }
  };

  const setUiFontPreference = (fontKey, options = {}) => {
    const { persist = false } = options;
    setUiFont(fontKey);
    if (persist && typeof window !== 'undefined') {
      try {
        localStorage.setItem('uiFontPreference', fontKey);
      } catch (e) {
        console.warn('⚠️ Failed to persist uiFontPreference to localStorage:', e);
      }
    }
  };

  // Complete email verification and create user profile
  const completeEmailVerification = async (email) => {
    console.group('✅ PRE-DEPLOYMENT AUTH: completeEmailVerification');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: completeEmailVerification');
    console.log('📧 Completing email verification for:', email);

    try {
      // Check if user is already signed in
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.email === email) {
        console.log('✅ User already signed in with this email');
        console.log('👤 Current user:', currentUser.uid);

        // Check their Firestore profile
        console.log('📊 Checking Firestore profile...');
        console.log('🗂️ Firestore path: users/' + currentUser.uid);
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('✅ Existing user data found:', userData);

          if (userData.passwordSet) {
            console.log('⚠️ User already has password set');
            console.log('📊 Registration status: Complete');
            console.groupEnd();
            return { success: false, error: 'User already registered. Please use login instead.' };
          } else {
            console.log('📍 User exists but needs password setup');
            console.log('📊 Registration status: Password setup required');
            setRegistrationStep('password');
            console.groupEnd();
            return { success: true };
          }
        } else {
          // Create profile for existing authenticated user
          console.log('📝 Creating new user profile in Firestore...');
          console.log('🗂️ Firestore path: users/' + currentUser.uid);
          const userData = {
            userEmail: currentUser.email,
            emailVerified: currentUser.emailVerified,
            passwordSet: false,
            assessmentComplete: false,
            profileComplete: false,
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp(),
            registrationStep: 'password'
          };

          await setDoc(userDocRef, userData);
          console.log('✅ User profile created for existing user');
          console.log('📦 Profile data:', userData);

          setRegistrationStep('password');
          console.groupEnd();
          return { success: true };
        }
      }

      // Try to use the email link
      console.log('🔗 Verifying email link...');
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        console.error('❌ Invalid sign-in link');
        throw new Error('Invalid sign-in link');
      }

      console.log('✅ Email link is valid, signing in...');
      const result = await signInWithEmailLink(auth, email, window.location.href);
      console.log('✅ Email verification successful');
      console.log('👤 User ID:', result.user.uid);
      console.log('📧 User email:', result.user.email);

      localStorage.removeItem('emailForSignIn');
      console.log('🗑️ Removed email from localStorage');

      // Check if user profile already exists
      console.log('📊 Checking for existing Firestore profile...');
      console.log('🗂️ Firestore path: users/' + result.user.uid);
      const userDocRef = doc(db, 'users', result.user.uid);
      const existingDoc = await getDoc(userDocRef);

      if (existingDoc.exists()) {
        const existingData = existingDoc.data();
        console.log('✅ User profile already exists:', existingData);

        if (existingData.passwordSet) {
          console.log('⚠️ User already has password set');
          console.log('📊 Registration status: Complete');
          console.groupEnd();
          return { success: false, error: 'User already registered. Please use login instead.' };
        } else {
          console.log('📍 User exists but needs password setup');
          console.log('📊 Registration status: Password setup required');
          setRegistrationStep('password');
          console.groupEnd();
          return { success: true };
        }
      } else {
        // Create initial user profile
        console.log('📝 Creating initial user profile in Firestore...');
        console.log('🗂️ Firestore path: users/' + result.user.uid);
        const userData = {
          userEmail: result.user.email,
          emailVerified: result.user.emailVerified,
          passwordSet: false,
          assessmentComplete: false,
          profileComplete: false,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          registrationStep: 'password'
        };

        await setDoc(userDocRef, userData);
        console.log('✅ User profile created');
        console.log('📦 Profile data:', userData);

        setRegistrationStep('password');
        console.groupEnd();
        return { success: true };
      }
    } catch (error) {
      console.error('❌ Error completing email verification:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      console.groupEnd();
      return { success: false, error: error.message };
    }
  };

  // Set user password
  const setupPassword = async (password) => {
    console.group('🔐 PRE-DEPLOYMENT AUTH: setupPassword');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: setupPassword');

    if (!user) {
      console.error('❌ No authenticated user');
      console.log('📊 User state: null');
      console.groupEnd();
      return { success: false, error: 'No authenticated user' };
    }

    console.log('👤 User:', user.uid);
    console.log('📧 Email:', user.email);

    try {
      console.log('🔐 Updating password in Firebase Auth...');
      await updatePassword(user, password);
      console.log('✅ Password updated successfully in Firebase Auth');

      // Update user profile
      console.log('📝 Updating user profile in Firestore...');
      console.log('🗂️ Firestore path: users/' + user.uid);
      const userDocRef = doc(db, 'users', user.uid);
      const updateData = {
        passwordSet: true,
        lastUpdated: serverTimestamp(),
        registrationStep: 'assessment'
      };

      await setDoc(userDocRef, updateData, { merge: true });
      console.log('✅ Password status updated in Firestore');
      console.log('📦 Update data:', updateData);

      // Update local state immediately
      setUserProfile(prevProfile => ({
        ...prevProfile,
        ...updateData,
        passwordSet: true
      }));
      console.log('✅ Local state updated');

      setRegistrationStep('assessment');
      console.log('📍 Registration step updated: assessment');
      console.log('✅ Password setup completed');
      console.groupEnd();
      return { success: true };
    } catch (error) {
      console.error('❌ Error setting password:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });

      if (error.code === 'auth/requires-recent-login') {
        console.log('⚠️ Recent login required - user needs to re-authenticate');
        console.groupEnd();
        return {
          success: false,
          error: 'Please click the email link again to continue setting your password.',
          requiresReauth: true
        };
      }

      console.groupEnd();
      return { success: false, error: error.message };
    }
  };

  // Complete Korean assessment
  const completeAssessment = async (assessmentData) => {
    console.group('📝 PRE-DEPLOYMENT AUTH: completeAssessment');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: completeAssessment');
    console.log('📦 Assessment data:', assessmentData);

    if (!user) {
      console.error('❌ No authenticated user');
      console.log('📊 User state: null');
      console.groupEnd();
      return { success: false, error: 'No authenticated user' };
    }

    console.log('👤 User:', user.uid);

    try {
      console.log('📝 Writing assessment data to Firestore...');
      console.log('🗂️ Firestore path: users/' + user.uid);
      const userDocRef = doc(db, 'users', user.uid);
      const updateData = {
        assessment: assessmentData,
        assessmentComplete: true,
        lastUpdated: serverTimestamp(),
        registrationStep: 'complete'
      };

      await setDoc(userDocRef, updateData, { merge: true });
      console.log('✅ Assessment data written to Firestore');
      console.log('📦 Update data:', updateData);

      // Update local state immediately
      setUserProfile(prevProfile => ({
        ...prevProfile,
        ...updateData,
        assessment: assessmentData,
        assessmentComplete: true
      }));
      console.log('✅ Local state updated');

      setRegistrationStep('complete');
      console.log('📍 Registration step updated: complete');
      console.log('✅ Assessment completed successfully');
      console.groupEnd();
      return { success: true };
    } catch (error) {
      console.error('❌ Error completing assessment:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      console.groupEnd();
      return { success: false, error: error.message };
    }
  };

  // Complete profile setup
  const completeProfile = async (additionalData = {}) => {
    console.group('✅ PRE-DEPLOYMENT AUTH: completeProfile');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: completeProfile');
    console.log('📦 Additional data:', additionalData);

    if (!user) {
      console.error('❌ No authenticated user');
      console.log('📊 User state: null');
      console.groupEnd();
      return { success: false, error: 'No authenticated user' };
    }

    console.log('👤 User:', user.uid);

    try {
      console.log('📝 Writing profile completion to Firestore...');
      console.log('🗂️ Firestore path: users/' + user.uid);
      const userDocRef = doc(db, 'users', user.uid);
      const updateData = {
        profileComplete: true,
        lastUpdated: serverTimestamp(),
        registrationStep: 'complete',
        ...additionalData // Include any additional data like username
      };

      await setDoc(userDocRef, updateData, { merge: true });
      console.log('✅ Profile completion written to Firestore');
      console.log('📦 Update data:', updateData);

      // Update local state immediately
      setUserProfile(prevProfile => ({
        ...prevProfile,
        ...updateData,
        profileComplete: true
      }));
      console.log('✅ Local state updated');

      console.log('✅ Profile completed successfully');
      console.groupEnd();
      return { success: true };
    } catch (error) {
      console.error('❌ Error completing profile:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      console.groupEnd();
      return { success: false, error: error.message };
    }
  };

  // Refresh user profile from Firestore
  const refreshProfile = async () => {
    if (!user) {
      console.log('⚠️ refreshProfile: No user to refresh');
      return null;
    }

    console.group('🔄 PRE-DEPLOYMENT AUTH: refreshProfile');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: refreshProfile');
    console.log('👤 Refreshing profile for user:', user.uid);
    console.log('🗂️ Firestore path: users/' + user.uid);

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('✅ Refreshed user data from Firestore');
        console.log('📦 User data:', userData);
        setUserProfile(userData);

        // Update registration step based on fresh data
        if (!userData.passwordSet) {
          console.log('📍 Registration step: password');
          setRegistrationStep('password');
        } else if (!userData.assessmentComplete) {
          console.log('📍 Registration step: assessment');
          setRegistrationStep('assessment');
        } else if (!userData.profileComplete) {
          console.log('📍 Registration step: complete');
          setRegistrationStep('complete');
        } else {
          console.log('📍 Registration step: dashboard');
          setRegistrationStep('dashboard');
        }

        console.groupEnd();
        return userData;
      } else {
        console.warn('⚠️ No user document found in Firestore');
      }
    } catch (error) {
      console.error('❌ Error refreshing profile:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
    }

    console.groupEnd();
    return null;
  };

  // Logout user implementation
  const logout = async () => {
    console.group('🚪 PRE-DEPLOYMENT AUTH: logout');
    console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
    console.log('🔧 Function: logout');
    try {
      console.log('📤 Attempting to sign out from Firebase...');
      await signOut(auth);

      // Clear persistent storage to ensure clean state
      localStorage.removeItem('emailForSignIn');
      console.log('🗑️ Cleared emailForSignIn from localStorage');

      setUser(null);
      setUserProfile(null);
      setRegistrationStep('email');
      console.log('✅ User signed out successfully');
      console.log('📊 State cleared: user, userProfile, registrationStep');
    } catch (error) {
      console.error('❌ Error signing out:', error);
      console.log('📊 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
    }
    console.groupEnd();
  };

  const value = {
    user,
    userProfile,
    loading,
    registrationStep,
    uiFont,
    isAdmin: userProfile?.isAdmin || false, // Add isAdmin property
    sendRegistrationLink,
    completeEmailVerification,
    setupPassword,
    completeAssessment,
    completeProfile,
    refreshProfile,
    setUiFontPreference,
    logout
  };



  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="loading-screen" style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #0a0a0c 100%)',
          gap: '1rem'
        }}>
          <div style={{ fontWeight: 800, letterSpacing: '0.35em', color: '#fff', fontSize: '1.4rem' }}>NUNCHI</div>
          <div className="loading-spinner"></div>
          <span style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)' }}>Loading...</span>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * ProtectedRoute Component
 * - Ensures user is authenticated before accessing a route
 * - Handles redirection to specific registration steps if not complete
 * - Shows loading state while auth is being initialized
 * - Integrated into AuthContext for centralized auth management
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to protect
 * @param {boolean} props.requireFullRegistration - Whether to enforce full registration (default: true)
 * @param {boolean} props.admin - Whether to require admin access (default: false)
 * @returns {React.ReactNode} Protected component or redirect
 */
export const ProtectedRoute = ({ children, requireFullRegistration = true, admin = false }) => {
  const { user, userProfile, loading, isAdmin } = useAuth();
  const location = useLocation();

  console.group(`🛡️ PROTECTED ROUTE: ${location.pathname}`);
  console.log('📁 File: src/pre-deployment/src/contexts/AuthContext.js v1.1');
  console.log('🔧 Function: ProtectedRoute');
  console.log('🎯 Route path:', location.pathname);
  console.log('👤 User authenticated:', !!user);
  console.log('📊 User ID:', user?.uid || 'null');
  console.log('📧 User email:', user?.email || 'null');
  console.log('⏳ Loading:', loading);
  console.log('🔒 Require full registration:', requireFullRegistration);
  console.log('👑 Require admin:', admin);

  if (userProfile) {
    console.log('📦 Profile status:', {
      passwordSet: userProfile.passwordSet,
      assessmentComplete: userProfile.assessmentComplete,
      profileComplete: userProfile.profileComplete,
      isAdmin: userProfile.isAdmin || false
    });
  } else {
    console.log('⚠️ No user profile available');
  }

  if (loading) {
    console.log('⏳ Auth loading, showing loading screen...');
    console.groupEnd();
    return (
      <div className="loading-screen" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#FAF1CA'
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Base authentication check
  if (!user) {
    console.log('❌ No user found');
    console.log('🔄 Redirecting to login page');
    console.log('📍 Redirect target: /');
    console.groupEnd();
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Admin access check
  if (admin) {
    if (!userProfile) {
      console.log('⚠️ No user profile found (admin check)');
      console.log('🔄 Redirecting to login page');
      console.log('📍 Redirect target: /');
      console.groupEnd();
      return <Navigate to="/" replace />;
    }

    if (!isAdmin) {
      console.log('❌ User is not an admin');
      console.log('👑 Admin required:', true);
      console.log('👑 User isAdmin:', isAdmin);
      console.log('🔄 Redirecting to homepage (access denied)');
      console.log('📍 Redirect target: /app-homepage');
      console.groupEnd();
      return <Navigate to="/app-homepage" replace />;
    }

    console.log('✅ Admin access granted');
  }

  // Registration step enforcement
  if (requireFullRegistration) {
    if (!userProfile) {
      // Profile still loading or doesn't exist? Redirect to email entry to be safe
      console.log('⚠️ No user profile found');
      console.log('🔄 Redirecting to login page');
      console.log('📍 Redirect target: /');
      console.groupEnd();
      return <Navigate to="/" replace />;
    }

    if (!userProfile.passwordSet && location.pathname !== '/setup-password') {
      console.log('❌ Password not set');
      console.log('🔄 Redirecting to password setup');
      console.log('📍 Redirect target: /setup-password');
      console.log('📊 Current path:', location.pathname);
      console.groupEnd();
      return <Navigate to="/setup-password" replace />;
    }

    if (userProfile.passwordSet && !userProfile.assessmentComplete && location.pathname !== '/assessment') {
      console.log('❌ Assessment not complete');
      console.log('🔄 Redirecting to assessment');
      console.log('📍 Redirect target: /assessment');
      console.log('📊 Current path:', location.pathname);
      console.groupEnd();
      return <Navigate to="/assessment" replace />;
    }

    if (userProfile.assessmentComplete && !userProfile.profileComplete && location.pathname !== '/complete') {
      console.log('❌ Profile not complete');
      console.log('🔄 Redirecting to profile completion');
      console.log('📍 Redirect target: /complete');
      console.log('📊 Current path:', location.pathname);
      console.groupEnd();
      return <Navigate to="/complete" replace />;
    }
  }

  console.log('✅ Route access granted');
  console.log('📊 Registration status: Complete');
  console.log('🎯 Rendering protected content');
  console.groupEnd();
  return children;
};
